import { isTemplateDependencyFunctionValidationError } from '@festina/configuration-general-types/configuration'
import { assertNotEmpty, type Optional } from '@festina-life/core'
import { Alert } from '@festina-life/ui/feedback'
import { useTranslate } from '@festina-life/ui/hooks'
import { useCallback, useMemo } from 'react'

import { useLayoutContext } from '../../../context/layoutContext'
import { useTemplateContext } from '../../../context/templateContext'
import { useComparableField } from '../../../context/useComparableField'
import { useIsErrorForField } from '../../../hooks/useIsErrorForField'
import type { ComparableValue } from '../../../types/diffing'
import {
  ConfigurationFieldType,
  type ConfigurationSimpleValueField,
  isConfigurationValueSetupField,
  isEnumeratedValueFrameFieldValue,
  isNotRelevantField,
  isSimpleOrConfigurationOnlyFieldValueField,
  isSimpleValueSetupField,
  isSubTemplatedField,
  isTemplateReferenceValueSetupField,
  isValueSetupFieldValue,
  type NonConfigurationSimpleValueField,
  type SimpleValueSetupField,
  type TemplateField,
  type TemplateReferenceValueSetupField,
  ValueSetupRole,
} from '../../../types/templateModel'
import {
  isInherited,
  isNotInherited,
} from '../../../uiModels/templateOperations/types'
import { isTemplateValidationErrorWithPath } from '../../../uiModels/types'
import { useOperationsContext } from '../../../workspace/operationsContext'
import { stringifyComparableValue } from '../../diffIndicators/helpers'
import {
  type SerialiseValueParams,
  ValueSetupDiffIndicators,
} from '../../diffIndicators/ValueSetupDiffIndicators'
import { TemplateDocumentationEntry } from '../../documentationEntry/TemplateDocumentationEntry'
import { SimpleFieldValue } from '../../simpleFieldValue/SimpleFieldValue'
import { TemplateFieldDocumentation } from '../../templateFieldDocumentation/TemplateFieldDocumentation'
import { TemplateFieldFrame } from '../../templateFieldFrame/TemplateFieldFrame'
import { TemplateFieldRequired } from '../../templateFieldRequired/TemplateFieldRequired'
import { mergeInheritedArrayValues } from '../../templateFieldsTable/fieldsHelpers'
import { TemplateFieldDynamicFunctions } from '../../templateFieldsTable/TemplateFieldDynamicFunctions'
import { TemplateFieldTypeDetails } from '../../templateFieldType/TemplateFieldTypeDetails'
import { TemplateFieldValidations } from '../../templateFieldValidations/TemplateFieldValidations'
import { TemplateReferenceFieldValue } from '../../templateReference/TemplateReferenceFieldValue'
import { SubTemplateFieldValueExpressionEntry } from './SubTemplateFieldValueExpressionEntry'

type Props = {
  field: TemplateField
}

/**
 * Displays all the properties of any template field.
 */
export const TemplateFieldContent = ({ field }: Props) => {
  const translate = useTranslate()
  const { errors } = useOperationsContext()
  const { isDraftTemplate, templateVersion, isMappingsTemplate } =
    useTemplateContext()
  const {
    preferences: {
      templates: { isFlattened },
    },
  } = useLayoutContext()
  const isErrorForField = useIsErrorForField()

  assertNotEmpty(templateVersion)

  const templateReferenceErrors = useMemo(
    () =>
      errors.filter(
        ({ error }) =>
          isErrorForField(field, error) &&
          isTemplateValidationErrorWithPath(error) &&
          (error.path ?? '').startsWith(`${field.origin}.variants`),
      ),
    [errors, field, isErrorForField],
  )

  const generalFieldErrors = useMemo(
    () =>
      errors.filter(
        ({ error }) =>
          isErrorForField(field, error) &&
          isTemplateValidationErrorWithPath(error) &&
          error.path === field.origin,
      ),
    [errors, field, isErrorForField],
  )

  const validationErrors = useMemo(
    () =>
      errors.filter(
        ({ error }) =>
          isErrorForField(field, error) &&
          isTemplateValidationErrorWithPath(error) &&
          (error.path ?? '').startsWith(`${field.origin}.validations`),
      ),
    [errors, field, isErrorForField],
  )

  const inScopeVariant =
    isSubTemplatedField(field) &&
    field.role === ValueSetupRole.SINGLE_CLASS_SUB_TEMPLATE
      ? field.value?.variants.at(0)
      : undefined

  const comparableField = useComparableField<TemplateField>(field.path)

  const defaultValueFromComparable = useCallback(
    ({
      value: comparable,
    }: SerialiseValueParams<
      SimpleValueSetupField | ConfigurationSimpleValueField
    >): string => {
      if (!comparable) {
        return ''
      }

      return (
        stringifyComparableValue(
          isSimpleValueSetupField(comparable)
            ? comparable.value?.defaultValue
            : comparable.value,
        ) ?? ''
      )
    },
    [],
  )

  const templateReferenceValueFromComparable = useCallback(
    ({
      value: comparable,
      valueType,
    }: SerialiseValueParams<TemplateReferenceValueSetupField>): string => {
      const currentValue =
        (comparableField.current as Optional<TemplateReferenceValueSetupField>)
          ?.value?.variants ?? []

      if (valueType === 'inherited') {
        const mergedValue = mergeInheritedArrayValues({
          currentValue,
          inheritedValue: comparable?.value?.variants ?? [],
          keyFn: (variant) =>
            (isMappingsTemplate ? variant.key : variant.source) ?? '',
        })

        return stringifyComparableValue(mergedValue)
      }

      return comparable?.value?.variants
        ? stringifyComparableValue(comparable.value?.variants)
        : ''
    },
    [comparableField, isMappingsTemplate],
  )

  const requiredValueFromComparable = useCallback(
    ({
      value: comparable,
    }: SerialiseValueParams<NonConfigurationSimpleValueField>) =>
      comparable ? stringifyComparableValue(comparable.value?.required) : '',
    [],
  )

  const requiredInModelInstanceValueFromComparable = useCallback(
    ({
      value: comparable,
    }: SerialiseValueParams<NonConfigurationSimpleValueField>) =>
      comparable
        ? stringifyComparableValue(comparable?.value?.requiredInModelInstance)
        : '',
    [],
  )

  const frameValueFromComparable = useCallback(
    ({ value: comparable }: SerialiseValueParams<TemplateField>) =>
      comparable && isSimpleValueSetupField(comparable)
        ? stringifyComparableValue(comparable.value?.frame)
        : '',
    [],
  )

  const dynamicFunctionsValueFromComparable = useCallback(
    ({ value: comparable }: SerialiseValueParams<TemplateField>) =>
      comparable && isSimpleValueSetupField(comparable)
        ? stringifyComparableValue(comparable.value?.dynamicFunctions)
        : '',
    [],
  )

  const validationsValueFromComparable = useCallback(
    ({ value: comparable, valueType }: SerialiseValueParams<TemplateField>) =>
      comparable &&
      isValueSetupFieldValue(comparable.value) &&
      isValueSetupFieldValue(field.value)
        ? // Validations are not diffed against in inherited values since they're always completely merged on flattening
          stringifyComparableValue(
            valueType === 'inherited'
              ? field.value.validations?.filter(isNotInherited)
              : comparable.value.validations,
          )
        : '',
    [field.value],
  )

  const fieldFrame = isSimpleValueSetupField(field) ? field.value?.frame : null
  let frameOrigin
  let framePath

  if (fieldFrame) {
    frameOrigin = isEnumeratedValueFrameFieldValue(fieldFrame)
      ? fieldFrame.entries?.origin
      : fieldFrame.range?.origin

    framePath = isEnumeratedValueFrameFieldValue(fieldFrame)
      ? fieldFrame.entries?.path
      : fieldFrame.range?.path
  }

  return (
    <>
      {generalFieldErrors.map(({ error }) => (
        <Alert
          key={error.errorMessage}
          severity="error"
          title={error.errorMessage ?? ''}
          className="col-span-2"
        />
      ))}
      {isNotRelevantField(field) && (
        <Alert
          severity="info"
          title={translate(
            'templateViewer.fieldDetailsModal.field.notRelevant.info',
          )}
          className="col-span-2"
        />
      )}
      <TemplateFieldDocumentation
        field={field}
        isExtendedView={false}
        areFieldsShownAsEditable={isDraftTemplate && !isNotRelevantField(field)}
      />
      {isSimpleOrConfigurationOnlyFieldValueField(field) && (
        <TemplateDocumentationEntry
          title={translate(
            'templateViewer.fieldDetailsModal.field.defaultValue',
          )}
          diffIndicator={
            <ValueSetupDiffIndicators
              comparableValues={
                comparableField as ComparableValue<
                  SimpleValueSetupField | ConfigurationSimpleValueField
                >
              }
              serialiseValueFn={defaultValueFromComparable}
              compareFilter="defaultValue"
            />
          }
          isShowingErrors
          errors={errors.filter(
            ({ error }) =>
              isErrorForField(field, error) &&
              isTemplateValidationErrorWithPath(error) &&
              !!error.path?.endsWith(
                isConfigurationValueSetupField(field) ? '' : 'defaultValue',
              ),
          )}
          value={<SimpleFieldValue field={field} isExpanded />}
          origin={
            isSimpleValueSetupField(field)
              ? field.value?.defaultValue?.origin
              : field.value?.origin
          }
          path={
            isSimpleValueSetupField(field)
              ? (field.value?.defaultValue?.path ??
                `${field.path}.defaultValue`)
              : field.path
          }
        />
      )}
      {isTemplateReferenceValueSetupField(field) && (
        <TemplateDocumentationEntry
          title={translate(
            'templateViewer.fieldDetailsModal.field.defaultValue',
          )}
          diffIndicator={
            <ValueSetupDiffIndicators
              comparableValues={
                comparableField as ComparableValue<TemplateReferenceValueSetupField>
              }
              serialiseValueFn={templateReferenceValueFromComparable}
              compareFilter="variants"
            />
          }
          isShowingErrors={isFlattened}
          errors={templateReferenceErrors}
          value={
            <TemplateReferenceFieldValue
              field={field}
              isExpanded
              // Error indexes don't work for flattened views so we show the erros at the header of the component instead
              errors={isFlattened ? [] : templateReferenceErrors}
            />
          }
          origin={field.origin}
          path={field.path}
        />
      )}
      {inScopeVariant && (
        <>
          <SubTemplateFieldValueExpressionEntry
            variant={inScopeVariant}
            property="defaultSelected"
          />
          <SubTemplateFieldValueExpressionEntry
            variant={inScopeVariant}
            property="selectable"
          />
        </>
      )}

      <TemplateFieldTypeDetails
        field={field}
        isCentered={false}
        errors={errors.filter(
          ({ error }) =>
            isErrorForField(field, error) &&
            isTemplateValidationErrorWithPath(error) &&
            !!error.path?.endsWith('valueConfig'),
        )}
        isExpanded
      />

      {!isConfigurationValueSetupField(field) && (
        <TemplateDocumentationEntry
          isShowingErrors
          errors={errors.filter(
            ({ error }) =>
              isErrorForField(field, error) &&
              isTemplateValidationErrorWithPath(error) &&
              !!error.path?.endsWith('required'),
          )}
          title={translate('templateViewer.fieldDetailsModal.field.required')}
          diffIndicator={
            <ValueSetupDiffIndicators
              comparableValues={
                comparableField as ComparableValue<NonConfigurationSimpleValueField>
              }
              serialiseValueFn={requiredValueFromComparable}
              compareFilter="required"
            />
          }
          value={<TemplateFieldRequired field={field} property="required" />}
          origin={field.value?.required?.origin}
          path={`${field.path}.required`}
        />
      )}
      {!isConfigurationValueSetupField(field) && (
        <TemplateDocumentationEntry
          isShowingErrors
          errors={errors.filter(
            ({ error }) =>
              isErrorForField(field, error) &&
              isTemplateValidationErrorWithPath(error) &&
              !!error.path?.endsWith('requiredInModelInstance'),
          )}
          title={translate(
            'templateViewer.fieldDetailsModal.field.requiredInModelInstance',
          )}
          diffIndicator={
            <ValueSetupDiffIndicators
              comparableValues={
                comparableField as ComparableValue<NonConfigurationSimpleValueField>
              }
              serialiseValueFn={requiredInModelInstanceValueFromComparable}
              compareFilter="requiredInModelInstance"
            />
          }
          value={
            <TemplateFieldRequired
              field={field}
              property="requiredInModelInstance"
            />
          }
          origin={field.value?.requiredInModelInstance?.origin}
          path={`${field.path}.requiredInModelInstance`}
        />
      )}
      {isSimpleValueSetupField(field) && (
        <>
          <TemplateDocumentationEntry
            isShowingErrors
            errors={errors.filter(
              ({ error }) =>
                isErrorForField(field, error) &&
                isTemplateValidationErrorWithPath(error) &&
                (!!error.path?.endsWith('entries') ||
                  !!error.path?.endsWith('range') ||
                  !!error.path?.endsWith('rangedFrame') ||
                  !!error.path?.endsWith('enumeratedFrame') ||
                  !!error.path?.endsWith('frame')),
            )}
            title={translate('templateViewer.fieldDetailsModal.field.frame')}
            diffIndicator={
              <ValueSetupDiffIndicators
                comparableValues={comparableField}
                serialiseValueFn={frameValueFromComparable}
                compareFilter="frame"
              />
            }
            value={<TemplateFieldFrame field={field} />}
            origin={frameOrigin}
            path={framePath ?? ''}
          />
          <TemplateDocumentationEntry
            isShowingErrors={false}
            errors={errors.filter(
              ({ error }) =>
                isErrorForField(field, error) &&
                isTemplateDependencyFunctionValidationError(error),
            )}
            title={translate(
              'templateViewer.fieldDetailsModal.field.dynamicFunctions',
            )}
            diffIndicator={
              <ValueSetupDiffIndicators
                comparableValues={comparableField}
                serialiseValueFn={dynamicFunctionsValueFromComparable}
                compareFilter="dynamicFunctions"
              />
            }
            value={<TemplateFieldDynamicFunctions field={field} />}
            origin={null}
            path={field.path}
          />
        </>
      )}
      {field.fieldType !==
        ConfigurationFieldType.ConfigurationExpressionValue && (
        <TemplateDocumentationEntry
          title={translate(
            'templateViewer.fieldDetailsModal.field.validations',
          )}
          diffIndicator={
            <ValueSetupDiffIndicators
              comparableValues={comparableField}
              serialiseValueFn={validationsValueFromComparable}
              compareFilter="validations"
              shouldHideInheritedDiff={
                !field.value?.validations?.some(isInherited)
              }
            />
          }
          isShowingErrors={isFlattened}
          errors={validationErrors}
          value={
            <TemplateFieldValidations
              field={field}
              // Error indexes don't work for flattened views so we show the erros at the header of the component instead
              errors={isFlattened ? [] : validationErrors}
            />
          }
          // Each validation presents its own inheritance indicator
          origin={null}
          path={field.path}
        />
      )}
    </>
  )
}
